<template>
    <div class="my-affiliate-page">
        <!-- My affiliate page -->

        <Navbar @setTopbarPadding="setTopbarPadding" />

        <div v-if="affiliateLoaded">

            <div class="container">
                <div v-if="croppingBanner" class="mb-3">
                    <vue-cropper
                        class="mb-3"
                        ref="bannerCropper"
                        :src="rawBanner"
                        preview=".preview"
                        :initial-aspect-ratio="1"
                        :aspect-ratio="16/9"
                    >
                    </vue-cropper>
                    <div class="d-flex">
                        <div class="ml-auto">
                            <button class='btn btn-primary' @click="saveBanner" :disabled="savingBanner">
                                <div v-if="savingBanner">
                                    <font-awesome-icon :icon="['fad', 'circle-notch']" spin />
                                </div>
                                <div v-else>Save Banner</div>
                            </button>
                            <button class='btn btn-light ml-2' @click="cancelBannerChange">
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <div
                        class="banner d-flex w-100"
                        :class="{'image': affiliate.profile_banner}"
                        :style="{backgroundImage: 'url(' + affiliate.profile_banner + ')'}"
                    >

                        <div v-if="savingBanner" class="d-flex h-100 w-100">
                            <div class="align-self-center mx-auto text-center">
                                <font-awesome-icon :icon="['fad', 'circle-notch']" size="2x" class="text-white" spin />
                            </div>
                        </div>

                        <div v-if="userAuthorized && !savingBanner" class="overlay h-100 w-100">
                            <div class="align-self-center mx-auto text-center">
                                <font-awesome-icon :icon="['fad', 'upload']" class="text-white" />
                                <div>Change</div>
                                <input type="file" class="input-file" :disabled="bannerInProgress" @change="bannerChange($event.target.files)" accept="image/*">
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="affiliate-bar navbar-expand navbar navbar-light bg-white border-top border-bottom py-4" :class="{'sticky': scrollPosition >= bannerHeight, 'shared': affiliate.shared && affiliate.shared.length}" v-sticky="{ zIndex: 10, stickyTop: topbarPadding}">
                <div class="container px-lg-3 px-0 d-md-flex d-block">

                    <div class="d-flex position-relative">
                        <div class="align-self-center profile-picture" :class="{'upload': userAuthorized}">

                            <div class="d-flex h-100 w-100">

                                <img :src="profile_picture" class="img-fluid rounded-circle">

                                <div v-if="userAuthorized" class="overlay h-100 w-100">
                                    <div class="align-self-center mx-auto">
                                        <font-awesome-icon :icon="['fad', 'upload']" class="text-white" />
                                        <div class="change">Change</div>
                                    </div>

                                    <input type="file" class="input-file" :disabled="portraitInProgress" @change="portraitChange($event.target.files)" accept="image/*">
                                </div>
                            </div>
                        </div>
                        <div class="align-self-center ml-3">
                            <h3 class="m-0">{{affiliate.first_name}} {{affiliate.last_name}}</h3>
                            <div v-if="affiliate.shared && affiliate.shared.length" class="lead text-wrap font-weight-normal mt-1">
                                Shared Studies Weekly to <strong class="text-warning">
                                    <span v-if="affiliate.shared && affiliate.shared.length">{{affiliate.shared}}</span>
                                    <span v-else>0</span>
                                </strong> people!
                            </div>
                        </div>
                    </div>
                    <ul class="affiliate-actions navbar-nav">
                        <div class="d-flex ml-auto">
                            <li v-if="affiliate.email" class="nav-item ml-auto">
                                <a class="btn btn-link text-nowrap" :href="'mailto:' + affiliate.email">
                                    <font-awesome-icon :icon="['fad', 'comment']" fixed-width /> Ask me a Question
                                </a>
                            </li>
                            <li class="nav-item ml-2">
                                <router-link :to="{name: 'home'}" class="btn btn-outline-primary text-nowrap" tabindex="-1" aria-disabled="true" exact>
                                    <font-awesome-icon :icon="['fad', 'shopping-cart']" fixed-width /> Buy Studies Weekly
                                </router-link>
                            </li>
                        </div>
                    </ul>
                </div>
            </div>

            <section class="p-5 bg-light">
                <div class="container">

                    <div v-if="userAuthorized" class="d-flex mb-3">
                        <div class="align-self-center">
                            <button type="button" class="btn btn-primary" @click.prevent="edit">
                                <font-awesome-icon :icon="['far', 'edit']" /> Edit Profile
                            </button>
                        </div>

                        <div class="align-self-center ml-auto">
                            <button v-if="userAuthorized" :disabled="editingPost" type="button" class="btn btn-outline-success" @click="addPost">
                                <font-awesome-icon :icon="['far', 'plus']" /> Add Post
                            </button>
                        </div>
                    </div>

                    <div v-if="croppingPortrait" class="mb-3">
                        <vue-cropper
                            class="mb-3"
                            ref="portraitCropper"
                            :src="rawPortrait"
                            preview=".preview"
                            :initial-aspect-ratio="1"
                            :aspect-ratio="1"
                        >
                        </vue-cropper>
                        <button class='btn btn-primary' @click="savePortrait" :disabled="savingPortrait">
                            <div v-if="savingPortrait">
                                <font-awesome-icon :icon="['fad', 'circle-notch']" spin />
                            </div>
                            <div v-else>Save Portrait</div>
                        </button>
                        <button class='btn btn-light ml-2' @click="cancelPortraitChange">
                            Cancel
                        </button>
                    </div>

                    <div class="row">
                        <div class="col-lg-4 order-lg-1 order-2 left-column">

                            <!-- ABOUT -->
                            <div class="card mb-3">
                                <h4 class="card-header d-flex">
                                    <div class="align-self-center">
                                        <font-awesome-icon :icon="['fad', 'info-circle']" class="text-primary" fixed-width /> About Me
                                    </div>
                                </h4>
                                <ul class="list-group list-group-flush">
                                    <li v-if="affiliate.original_start_date" class="list-group-item d-flex">
                                        <div class="align-self-center">
                                            <font-awesome-icon :icon="['fad', 'calendar']" fixed-width />
                                        </div>
                                        <div class="align-self-center ml-3">
                                            Using Studies Weekly since <span class="text-warning">{{using_since}}</span>
                                        </div>
                                    </li>
                                    <li v-if="affiliate.current_school" class="list-group-item d-flex">
                                        <div class="align-self-center">
                                            <font-awesome-icon :icon="['fad', 'school']" fixed-width />
                                        </div>
                                        <div class="align-self-center ml-3">
                                            Working at <span class="text-warning">{{affiliate.current_school}}</span><span v-if="affiliate.current_role"> as a <span class="text-warning">{{affiliate.current_role}}</span></span>
                                        </div>
                                    </li>
                                    <li v-if="affiliate.city || affiliate.state" class="list-group-item d-flex">
                                        <div class="align-self-center">
                                            <font-awesome-icon :icon="['fad', 'map-marker']" fixed-width />
                                            <!-- <font-awesome-icon :icon="['fad', 'location']" fixed-width /> -->
                                        </div>
                                        <div class="align-self-center ml-3">
                                            Lives in <span v-if="affiliate.city"><span class="text-warning">{{affiliate.city}}</span>, </span><span class="text-warning">{{affiliate.state}}</span>
                                        </div>
                                    </li>
                                    <li v-for="(degree, index) in affiliate.degrees" :key="index" class="list-group-item d-flex">
                                        <div class="align-self-center">
                                            <font-awesome-icon :icon="['fad', 'graduation-cap']" fixed-width />
                                        </div>
                                        <div class="align-self-center ml-3">
                                            <span class="text-warning">{{degree.degree}}</span> from <span class="text-warning">{{degree.earned_from}}</span>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                        </div>
                        <div class="col-lg-8 order-lg-2 order-1 right-column mb-lg-0 mb-3">

                            <div class="alert alert-warning">
                                <h4 class="alert-heading">
                                    <font-awesome-icon :icon="['fad', 'heart']" class="text-danger" /> Why I love Studies Weekly
                                </h4>
                                <div v-if="affiliate.banner_comment">
                                    {{affiliate.banner_comment}}
                                </div>
                                <div v-else class="text-danger">
                                    No message added yet
                                </div>
                            </div>

                            <div v-for="(post, index) in affiliate.posts" :key="index" class="post card">
                                <h4 class="card-header d-flex">
                                    <div v-if="post.editing" class='align-self-center flex-grow-1'>
                                        <input :ref="'post' + index + 'Title'" type='text' v-model='post.title' class='form-control' placeholder='Post Title'>
                                    </div>
                                    <div v-else class="align-self-center">
                                        {{post.title}}
                                    </div>
                                    <div v-if="userAuthorized && !post.editing" class='ml-auto'>
                                        <button class='btn btn-primary' @click="editPost(post)" :disabled="editingPost" v-b-popover.hover="'Edit'">
                                            <font-awesome-icon :icon="['fad', 'edit']" />
                                        </button>
                                        <button class='btn btn-outline-danger ml-1' @click='deletePost(post)' v-b-popover.hover="'Delete'">
                                            <font-awesome-icon :icon="['fad', 'trash']" />
                                        </button>
                                    </div>
                                </h4>
                                <div class="card-body">
                                    <div v-if="post.editing">
                                        <editor-menu-bar :editor="post.editor" v-slot="{ commands, isActive }">
                                            <div>
                                                <div class="btn-group mb-3">
                                                    <button class="btn btn-sm btn-light" :class="{ 'active': isActive.bold() }" @click="commands.bold">
                                                        <font-awesome-icon :icon="['far', 'bold']" />
                                                    </button>
                                                    <button class="btn btn-sm btn-light" :class="{ 'active': isActive.italic() }" @click="commands.italic">
                                                        <font-awesome-icon :icon="['far', 'italic']" />
                                                    </button>
                                                    <button class="btn btn-sm btn-light" :class="{ 'active': isActive.underline() }" @click="commands.underline">
                                                        <font-awesome-icon :icon="['far', 'underline']" />
                                                    </button>
                                                    <button class="btn btn-sm btn-light" :class="{ 'active': isActive.strike() }" @click="commands.strike">
                                                        <font-awesome-icon :icon="['far', 'strikethrough']" />
                                                    </button>
                                                </div>
                                                <div class="btn-group mb-3 ml-2">
                                                    <button class="btn btn-sm btn-light" :class="{ 'active': isActive.bullet_list() }" @click="commands.bullet_list">
                                                        <font-awesome-icon :icon="['far', 'list-ul']" />
                                                    </button>
                                                    <button class="btn btn-sm btn-light" :class="{ 'active': isActive.ordered_list() }" @click="commands.ordered_list">
                                                        <font-awesome-icon :icon="['far', 'list-ol']" />
                                                    </button>
                                                </div>
                                                <div class="btn-group mb-3 ml-2">
                                                    <button class="btn btn-sm btn-light" :class="{ 'active': isActive.heading({ level: 1 }) }" @click="commands.heading({ level: 1 })">
                                                        <font-awesome-icon :icon="['far', 'h1']" />
                                                    </button>
                                                    <button class="btn btn-sm btn-light" :class="{ 'active': isActive.heading({ level: 2 }) }" @click="commands.heading({ level: 2 })">
                                                        <font-awesome-icon :icon="['far', 'h2']" />
                                                    </button>
                                                    <button class="btn btn-sm btn-light" :class="{ 'active': isActive.heading({ level: 3 }) }" @click="commands.heading({ level: 3 })">
                                                        <font-awesome-icon :icon="['far', 'h3']" />
                                                    </button>
                                                </div>
                                                <div class="btn-group mb-3 ml-2">
                                                    <button class="btn btn-sm btn-light" @click="showImagePrompt(commands.image)">
                                                        <font-awesome-icon :icon="['far', 'images']" />
                                                    </button>
                                                </div>
                                            </div>
                                        </editor-menu-bar>

                                        <editor-content :editor="post.editor" class="border rounded p-3 editor-body" />
                                    </div>
                                    <div v-else v-html="post.content"></div>
                                </div>
                                <div v-if="post.editing" class="card-footer d-flex">
                                    <div class="ml-auto">
                                        <button class='btn btn-light' @click="cancelPost(post)">Cancel</button>
                                        <button class='btn btn-primary ml-1' @click="savePost(post)" :disabled="!post.title">Save</button>
                                    </div>
                                </div>
                            </div>

                            <div v-if="userAuthorized || affiliate.testimonials && affiliate.testimonials.length" class="card">
                                <h4 class="card-header d-flex">
                                    <div class="align-self-center">
                                        <font-awesome-icon :icon="['fad', 'user']" class="text-success" fixed-width /> What People Say About Studies Weekly
                                    </div>
                                </h4>
                                <div class="card-body">
                                    <div v-if="affiliate.testimonials && affiliate.testimonials.length">
                                        <carousel
                                            :per-page="1"
                                            :mouse-drag="true"
                                            :autoplay="true"
                                            :autoplayTimeout="30000"
                                            :loop="true"
                                        >
                                            <slide v-for="(testimonial, index) in affiliate.testimonials" :key="index" class="bg-light p-3 rounded border">
                                                <blockquote class="mb-0">
                                                    <p>{{testimonial.comment}}</p>
                                                    <footer class="blockquote-footer">{{testimonial.comment_from}}</footer>
                                                </blockquote>
                                            </slide>
                                        </carousel>
                                    </div>
                                    <div v-else class="text-danger">
                                        0 testimonials added
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <b-modal id="edit-modal" size="xl" :ok-title="'Save Changes'" :ok-disabled="savingProfile" @ok="saveProfile">
                <template v-slot:modal-header>
                    <div class="d-flex w-100">
                        <h4 class="align-self-center my-0">
                            Edit Profile
                        </h4>
                        <div class="align-self-center ml-auto">
                            <button type="button" class="close" aria-label="Close" @click="closeModal('edit-modal')">
                                <font-awesome-icon :icon="['fas', 'times']" />
                            </button>
                        </div>
                    </div>
                </template>
                <b-tabs content-class="mt-3">
                    <b-tab title="About Me" active>

                        <div class="form-group">
                            <label for="firstName">First Name</label>
                            <input ref="firstName" type="text" class="form-control" v-model="editableAffiliate.first_name" />
                        </div>

                        <div class="form-group">
                            <label for="lastName">Last Name</label>
                            <input ref="lastName" type="text" class="form-control" v-model="editableAffiliate.last_name" />
                        </div>

                        <div class="form-group">
                            <label for="firstName">Using Studies Weekly Since: {{using_since}}</label>
                            <b-form-datepicker id="original_start_date" v-model="editableAffiliate.original_start_date" class="mb-2"></b-form-datepicker>
                        </div>


                        <div class="form-group">
                            <label for="bannerComment">Bio (Why I love Studies Weekly)</label>
                            <textarea ref="bannerComment" class="form-control" rows="4" v-model="editableAffiliate.banner_comment" />
                        </div>

                        <div class="form-group">
                            <label for="currentSchool">Working At</label>
                            <input ref="currentSchool" type="text" class="form-control" v-model="editableAffiliate.current_school" />
                        </div>

                        <div class="form-group">
                            <label for="current_role">Role</label>
                            <input ref="current_role" name="current_role" type="text" class="form-control" formnovalidate v-model="editableAffiliate.current_role" />
                        </div>

                        <div class="form-group">
                            <label for="city">City</label>
                            <input ref="city" type="text" class="form-control" v-model="editableAffiliate.city" />
                            <div class="invalid-feedback">
                                City is required
                            </div>
                        </div>

                        <div class="form-group mb-0">
                            <label for="state">State</label>
                            <select ref="state" type="text" class="form-control" v-model="editableAffiliate.state">
                                <option v-for="(state, index) in states" :key="index" :value="state.state">
                                    {{ state.fullState }}
                                </option>
                            </select>
                            <div class="invalid-feedback">
                                State is required
                            </div>
                        </div>
                    </b-tab>

                    <b-tab title="Degrees">

                        <div class="mb-3">
                            <div v-for="(degree, index) in editableAffiliate.degrees" :key="index" class="degree card">

                                <a href="#" class="remove btn btn-danger text-white rounded-circle" @click.prevent="removeDegree(index)">
                                    <font-awesome-icon :icon="['fas', 'times']" />
                                </a>

                                <div class="card-body">
                                    <div class="d-flex">
                                        <div class="flex-grow-1 align-self-center">
                                            <input type="hidden" v-model="degree.id" ref="id" />

                                            <div class="form-group">
                                                <label :for="'degree' + degree.id">Degree</label>
                                                <input :ref="'degree' + degree.id" type="text" class="form-control" v-model="degree.degree">
                                            </div>

                                            <div class="form-group mb-0">
                                                <label for="earned_from">Earned From</label>
                                                <input type="text" v-model="degree.earned_from" ref="earned_from" class="form-control" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="!editableAffiliate.degrees || editableAffiliate.degrees && !editableAffiliate.degrees.length" class="text-danger mb-3">
                            0 degrees added
                        </div>

                        <button type="button" class="btn btn-sm btn-outline-primary" @click="addDegree">
                            <font-awesome-icon :icon="['far', 'plus']" /> Add Degree
                        </button>

                    </b-tab>
                    <b-tab title="Testimonials">

                        <div class="mb-3">
                            <div class="testimonial card" v-for="(testimonial, index) in editableAffiliate.testimonials" :key="index">

                                <a href="#" class="remove btn btn-danger text-white rounded-circle" @click.prevent="removeTestimonial(index)">
                                    <font-awesome-icon :icon="['fas', 'times']" />
                                </a>

                                <div class="card-body">
                                    <div class="d-flex">
                                        <div class="flex-grow-1 align-self-center">
                                            <div class="form-group mb-0">
                                                <label :for="'testimonial' + index">
                                                    Testimonial
                                                </label>
                                                <textarea :ref="'testimonial_comment' + index" class="form-control" v-model="testimonial.comment" />
                                            </div>
                                            <div class="form-group mb-0">
                                                <label :for="'by' + index">
                                                    By
                                                </label>
                                                <input type="text" class="form-control" v-model="testimonial.comment_from">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div v-if="!editableAffiliate.testimonials || editableAffiliate.testimonials && !editableAffiliate.testimonials.length" class="text-danger mb-3">
                            0 testimonials added
                        </div>

                        <button type="button" class="btn btn-sm btn-outline-primary" @click="addTestimonial">
                            <font-awesome-icon :icon="['far', 'plus']" /> Add Testimonial
                        </button>

                    </b-tab>
                </b-tabs>
            </b-modal>
        </div>
        <div v-else>

        </div>

        <Footer />

    </div>
</template>

<style lang="scss">
.VueCarousel-dot-container {
    margin: 0 !important;
    button {
        margin-top: 15px !important;
    }
}
.editor-body {
    min-height: 100px;
}

.ProseMirror {
    min-height: 87px;
    outline: 0;
}
</style>

<style lang="scss" scoped>
@import "@/assets/scss/_colors.scss";

.adding-post {
    opacity: .25;
    filter: grayscale(1);
}

#edit-modal {
    .card {
        margin: 30px 15px 15px 0;
        .remove {
            position: absolute;
            top: 0;
            right: 0;
            margin: -15px;
        }
    }

    .card + .card {
        margin-top: 30px;
    }
}

.card + .card {
    margin-top: 1rem;
}

.banner {
    position: relative;
    background-color: $secondary;

    // height: 100px;

    height: 200px;

    &.image {
        background-repeat: no-repeat;
        background-size: cover;
        height: 350px;
    }

    .overlay {
        display: none;
        cursor: pointer;
    }

    &:hover .overlay {
        display: flex;
        position: absolute;
        background-color: rgba(0, 0, 0, .5);
        color: #FFFFFF;
    }

    .input-file {
        opacity: 0; /* invisible but it's there! */
        width: 100%;
        height: 350px;
        position: absolute;
        top: 0;
        left: 0;
        cursor: pointer;
    }
}

@media (max-width: 991px) {
    .banner.image {
        height: 300px;
        & .input-file {
            height: 300px;
        }
    }
}

@media (max-width: 767px) {
    .banner.image {
        height: 200px;
        & .input-file {
            height: 200px;
        }
    }
}

.profile-picture {
    position: relative;

    &.upload:hover {
        cursor: pointer;
        background-color: rgba(0, 0, 0, .75);
    }

    & .overlay {
        display: none;
    }

    &:hover .overlay {
        position: absolute;
        background-color: rgba(0, 0, 0, .5);
        color: #FFFFFF;
        border-radius: 100%;
        display: flex;
    }

    & .input-file {
        opacity: 0; /* invisible but it's there! */
        width: 150px;
        height: 150px;
        position: absolute;
        top: 0;
        left: 0;
        cursor: pointer;
    }

    margin: -150px 0;

    height: 150px;
    width: 150px;
    min-height: 150px;
    min-width: 150px;
    border: 3px solid $light;
    transition: ease .25s;
    text-align: center;
    border-radius: 100%;

    background-color: $light;

    overflow: hidden;
}

.affiliate-bar.sticky {
    .profile-picture {

        margin: -50px 0;
        width: 60px;
        height: 60px;
        min-height: 60px;
        min-width: 60px;

        &.shared {
            width: 75px;
            height: 75px;
            min-height: 75px;
            min-width: 75px;
        }
    }
    .change {
        display: none;
    }
}

@media (max-width: 767px) {
    .profile-picture {

        margin: -50px 0;
        width: 60px;
        height: 60px;
        min-height: 60px;
        min-width: 60px;

        &.shared {
            width: 75px;
            height: 75px;
            min-height: 75px;
            min-width: 75px;
        }
    }
    .change {
        display: none;
    }
}

@media (max-width: 991px) {
    .affiliate-actions {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 15;
        background-color: $light;
        padding: 1rem;
        border-top: 1px solid #dee2e6;
        border-bottom: 1px solid #dee2e6;
    }
}
</style>

<script>
import Vue from 'vue'

import { mapState } from 'vuex'

import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'

import VueSticky from 'vue-sticky'

import moment from 'moment'

Vue.use(require('vue-moment'))

import states from '@/common/states'

import Navbar from '@/components/Navbar'
import Footer from '@/components/Footer'
import { Editor, EditorContent, EditorMenuBar } from 'tiptap'
import {
    Bold,
    Italic,
    Underline,
    Image,
    OrderedList,
    BulletList,
    ListItem,
    Strike,
    Link,
    Heading,
    HorizontalRule
} from 'tiptap-extensions'

export default {
    name: 'my-affiliate-page',
    data: () => ({
        states: states,
        testimonialTypes: ["teacher", "student"],

        affiliate: {},

        editableAffiliate: {},

        portrait: null,

        topbarPadding: null,
        scrollPosition: 0,

        savingProfile: false,

        savingBanner: false,

        addingPost: false,

        rawBanner: null,
        bannerInProgress: false,
        croppingBanner: false,
        savingBanner: false,

        rawPortrait: null,
        portraitInProgress: false,
        croppingPortrait: false,
        savingPortrait: false,

        affiliateLoaded: false
    }),
    props: [
        'affiliate_code'
    ],
    computed: mapState({
        user () {
            return this.$auth.user()
        },
        editingPost() {
            return _.reduce(this.affiliate.posts, (p, n) => {return p || n.editing}, false )
        },
        userAuthorized () {
            let userAuthorized = false
            if (
                (this.user && this.user.id) &&
                (this.affiliate && this.affiliate.user_id) &&
                this.affiliate.user_id == this.user.id
            ) {
                this.affiliate
                userAuthorized = true
            }
            return userAuthorized
        },
        profile_picture () {
            if (this.affiliate && this.affiliate.portrait) {
                return this.affiliate.portrait
            } else {
                return "//cdn.studiesweekly.com/img/default_profile.jpg"
            }
        },
        using_since () {
            var original_start_date = null
            if (this.affiliate && this.affiliate.original_start_date) {
                original_start_date = moment(this.affiliate.original_start_date).format("MMMM YYYY")
            }
            return original_start_date
        },
        created_at () {
            let created_at = null
            if (this.affiliate && this.affiliate.created_at) {
                created_at = moment(this.affiliate.created_at).format("MMMM YYYY")
            }
            return created_at
        },
        bannerHeight () {
            // if (this.affiliate.profile_banner) {
            //     return 150
            // } else {
            //     return 75
            // }
            return 100
        },
        swiper () {
            return this.$refs.mySwiper.swiper
        },
    }),
    components: {
        Navbar,
        Footer,

        VueCropper,
        EditorContent,
        EditorMenuBar,
    },
    directives: {
        'sticky': VueSticky,
    },
    methods: {
        deletePost(post) {
            this.$swal.fire({
                text: 'Are you sure you want to delete this post?',
                type: 'warning',
                showCancelButton: true,
                // confirmButtonColor: '#3085d6',
                // cancelButtonColor: '#d33',
                // confirmButtonText: 'Yes, delete it!'
                confirmButtonText: 'Delete Post',
                confirmButtonClass: 'btn btn-danger',
                cancelButtonText: 'Cancel',
                cancelButtonClass: 'btn btn-light',
                buttonsStyling: false,
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.$http({
                        'url': 'dashboard/affiliate/post/delete',
                        'method': 'post',
                        'data': {
                            'id': post.id
                        }
                    }).then((response) => {
                        post.id = null
                        this.cancelPost(post)

                        this.$swal({
                            toast: true,
                            html: 'Post Deleted',
                            type: 'success',
                            position: 'top-end',
                            showConfirmButton: false,
                            timer: 3000
                        })
                    }).catch(error => {
                        this.$swal({
                            text: error.message,
                            type: 'error'
                        })
                    })
                }
            })
        },
        cancelPost(post) {
            this.addingPost = false
            post.editing = false

            if (post.editor) {
                post.editor.destroy()
            }

            delete post.editor
            if(!post.id){
                this.affiliate.posts.splice(this.affiliate.posts.indexOf(post), 1)
            }
        },
        editPost(post) {
            Vue.set(post, 'editing', true)
            Vue.set(post, 'editor', this.newTipTap(post.content))
        },
        savePost(post) {
            this.$http({
                'url': 'dashboard/affiliate/post/save',
                'method': 'post',
                'data': {
                    'post': {
                        user_id: this.affiliate.user_id,
                        title: post.title,
                        content: post.editor.getHTML(),
                        id: post.id || null
                    }
                }
            }).then((response) => {
                post.id = response.data.post.id
                post.content = response.data.post.content
                this.$swal({
                    toast: true,
                    html: 'Changes saved',
                    type: 'success',
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                })
                this.cancelPost(post)
                //this.$bvModal.hide("edit-modal")
            }).catch(error => {
                this.$swal({
                    text: error.message,
                    type: 'error'
                })
            })
        },
        showImagePrompt(command) {
            console.log(command)
            const src = prompt('Enter the url of your image here')
            if (src !== null) {
                command({ src })
            }
        },
        getAffiliate () {
            this.affiliateLoaded = false

            this.$http({
                'url': 'affiliate/' + this.affiliate_code,
                'method': 'get'
            }).then((response) => {
                if (response.data.success) {
                    this.affiliate = response.data.affiliate

                    this.$store.commit('setAffiliateId', this.affiliate.user_id)
                    this.$store.commit('setAffiliateName', `${this.affiliate.first_name} ${this.affiliate.last_name}`)
                    this.$store.commit('setAffiliatePortrait', this.affiliate.portrait)


                    if (!this.affiliate.posts) {
                        this.$set(this.affiliate, "posts", [])
                    }

                    this.affiliateLoaded = true
                    for(let post of this.affiliate.posts){
                        post.editor = new Editor({
                            content: post.body,
                        })
                    }
                }
            }).catch(error => {
                this.$swal({
                    text: error.data.message,
                    type: 'warning'
                })
            })
        },
        edit () {
            this.$bvModal.show("edit-modal")

            this.editableAffiliate = _.cloneDeep(this.affiliate)
        },
        saveProfile (event) {
            event.preventDefault()

            this.savingProfile = true

            let postData = {
                // id: this.affiliate.id,
                original_start_date: this.editableAffiliate.original_start_date,
                user_id: this.editableAffiliate.user_id,
                first_name: this.editableAffiliate.first_name,
                last_name: this.editableAffiliate.last_name,
                // profile_banner: this.editableAffiliate.profile_banner,
                current_school: this.editableAffiliate.current_school,
                current_role: this.editableAffiliate.current_role,
                city: this.editableAffiliate.city,
                state: this.editableAffiliate.state,
                banner_comment: this.editableAffiliate.banner_comment,
                degrees: this.editableAffiliate.degrees,
                // posts: _.map(this.editableAffiliate.posts, p => {return {title: p.title, body: p.editor.getHTML()}}),
                testimonials: this.editableAffiliate.testimonials,
            }

            this.$http({
                'url': 'dashboard/affiliate/save',
                'method': 'post',
                'data': {
                    'affiliate': postData
                }
            }).then((response) => {
                this.savingProfile = false
                this.affiliate = response.data.affiliate

                this.$swal({
                    toast: true,
                    html: 'Changes saved',
                    type: 'success',
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                })

                this.$bvModal.hide("edit-modal")
            }).catch(error => {
                this.savingProfile = false

                this.$swal({
                    text: error.message,
                    type: 'error'
                })
            })
        },
        newTipTap (content) {
            return new Editor({
                content: content,
                extensions: [
                    new Bold(),
                    new Italic(),
                    new Underline(),
                    new Image(),
                    new BulletList(),
                    new OrderedList(),
                    new Strike(),
                    new Heading({ levels: [1, 2, 3] }),
                    new Link(),
                    new HorizontalRule(),
                    new ListItem(),
                ]
            })
        },
        addTestimonial () {
            if (
                !this.editableAffiliate.testimonials ||
                this.editableAffiliate.testimonials && !this.editableAffiliate.testimonials.length
            ) {
                this.editableAffiliate.testimonials = []
            }

            this.editableAffiliate.testimonials.push({})
        },
        removeTestimonial (index) {
            this.editableAffiliate.testimonials.splice(index, 1)
        },
        addPost () {
            if (
                !this.editableAffiliate.posts ||
                this.editableAffiliate.posts && !this.editableAffiliate.posts.length
            ) {
                this.editableAffiliate.posts = []
            }

            this.affiliate.posts.unshift({
                title: '',
                content: '',
                editor: this.newTipTap(''),
                editing: true,
                id: null
            })

            // this.$refs.postTitle.focus()

            this.addingPost = true
        },
        removePost (index) {
            this.editableAffiliate.posts.splice(index, 1)
        },
        addDegree () {
            if (
                !this.editableAffiliate.degrees ||
                this.editableAffiliate.degrees && !this.editableAffiliate.degrees.length
            ) {
                this.editableAffiliate.degrees = []
            }

            this.editableAffiliate.degrees.push({})
        },
        removeDegree (index) {
            this.editableAffiliate.degrees.splice(index, 1)
        },
        bannerChange (fileList) {
            if (fileList && fileList[0]) {
                this.bannerInProgress = true

                let FR = new FileReader()

                FR.addEventListener("load", (e) => {
                    this.rawBanner = e.target.result

                    this.croppingBanner = true
                })

                FR.readAsDataURL(fileList[0])
            }
        },
        cancelBannerChange () {
            this.bannerInProgress = false
            this.croppingBanner = false
        },
        saveBanner (e) {
            var formData = new FormData()

            this.$refs.bannerCropper.getCroppedCanvas({width: 1160, height: 350}).toBlob(blob => {

                formData.append("banner", blob)

                this.savingBanner = true

                this.$http({
                    'url': `dashboard/affiliate/saveBanner`,
                    'method': 'POST',
                    'data': formData,
                    'headers': {
                        'Content-Type': 'multipart/form-data'
                    }
                }).then((response) => {
                    if (response.data.success) {
                        var url = null

                        if (response.data.url) {
                            url = response.data.url
                        }

                        this.$set(this.affiliate, 'profile_banner', url + '?' + Date.now())

                        this.croppingBanner = false
                        this.savingBanner = false
                        this.bannerInProgress = false
                    }
                }).catch(error => {
                    this.savingBanner = false

                    this.$swal({
                        title: "Error",
                        text: error.message,
                        type: 'error'
                    })
                })
            })
        },
        portraitChange (fileList) {
            if (fileList && fileList[0]) {
                this.portraitInProgress = true

                let FR = new FileReader()

                FR.addEventListener("load", (e) => {
                    this.rawPortrait = e.target.result

                    this.croppingPortrait = true
                })

                FR.readAsDataURL(fileList[0])
            }
        },
        cancelPortraitChange () {
            this.portraitInProgress = false

            this.croppingPortrait = false
        },
        savePortrait (e) {
            let dataURL = this.$refs.portraitCropper.getCroppedCanvas({width: 500, height: 500}).toDataURL()

            this.savingPortrait = true

            this.$http({
                'url': `dashboard/affiliate/savePortrait`,
                'method': 'POST',
                'data': {
                    'portrait': dataURL
                }
            }).then((response) => {
                if (response.data.success) {
                    var url = null

                    if (response.data.url) {
                        url = response.data.url
                    }

                    this.$set(this.affiliate, 'portrait', url)
                } else {
                    this.$swal({
                        text: response.data.message,
                        type: 'warning'
                    })
                }
                this.savingPortrait = false
                this.croppingPortrait = false
                this.portraitInProgress = false

                this.$bvModal.hide("crop-modal")
            }).catch(error => {
                this.savingPortrait = false

                this.$swal({
                    title: "Error",
                    text: error.message,
                    type: 'error'
                })
            })
        },
        setTopbarPadding (event) {
            this.topbarPadding = event
        },
        closeModal (modal) {
            this.$bvModal.hide(modal)
        }
    },
    mounted () {
        this.getAffiliate()

        window.addEventListener("scroll", () => {
            this.scrollPosition = window.scrollY
        })
    }
}
</script>
